/**
 * Client detail — everything the brokerage holds on one client, in one place.
 *
 * Before this, the Clients screen derived its rows from dossier summaries and
 * showed a name and nothing else: there was no way to see a client's contact
 * details, and no way to OPEN a document they had uploaded (document-svc only
 * ever signed upload PUTs, so the files were write-only to every role).
 *
 * Scoping is the server's: admins reach any client in the tenant, agents only
 * their own, and an out-of-scope id 404s — this page just renders what it is
 * given and shows the 404 plainly.
 */

import { useEffect, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";

import {
  ApiClientError,
  clientService,
  documentService,
  dossierService,
  openDocument,
  type ClientDetail as ClientDetailRow,
} from "@re-platform/api-client";
import type {
  ClassificationStatus,
  Document,
  Dossier,
  DossierSlot,
  DossierSummary,
} from "@re-platform/types";
import {
  Button,
  Card,
  StatusPill,
  type StatusTone,
  Toast,
} from "@re-platform/ui";

import { api } from "../../lib/api";
import { ContactButtons, formatPhone } from "../../lib/contacts";

function fullName(c: ClientDetailRow): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return name || c.email || c.id.slice(0, 8);
}

/** "4210 RED OAK TRAIL, ROUND ROCK, TX 78681" — omitting whatever is missing. */
function formatAddress(c: ClientDetailRow): string | null {
  const cityLine = [c.address_city, c.address_state].filter(Boolean).join(", ");
  const tail = [cityLine, c.address_zip].filter(Boolean).join(" ");
  const parts = [c.address_street, tail].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
}

/**
 * Documents the AI couldn't confidently file away — surfaced here so the agent
 * knows to open Active dossiers and approve or reject the slot. `null` when the
 * doc is cleanly classified and needs no human eyes.
 *
 * This is the DOCUMENT's classification only. The checklist item's verdict (an
 * AI rejection, or a document waiting on the agent) lives on the dossier slot
 * and is read separately — see `verdictsFor` (spec 021).
 */
function reviewBadge(
  status: ClassificationStatus,
): { label: string; tone: StatusTone } | null {
  switch (status) {
    case "DECLARED_MISMATCH":
      return { label: "AI flagged · wrong type", tone: "danger" };
    case "CLASSIFIED_PENDING_REVIEW":
      return { label: "Needs review", tone: "warning" };
    default:
      return null;
  }
}

const labelStyle: React.CSSProperties = {
  color: "var(--rp-color-text-muted)",
  fontSize: 12,
  marginBottom: 2,
};

function Field({
  label,
  value,
  note,
}: {
  label: string;
  value: string | null;
  note?: string;
}) {
  return (
    <div style={{ minWidth: 200 }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: 14 }}>
        {value ?? (
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            Not on file
          </span>
        )}
      </div>
      {note && value && (
        <div style={{ ...labelStyle, marginTop: 2, marginBottom: 0 }}>
          {note}
        </div>
      )}
    </div>
  );
}

/** Slot statuses that put a document in front of the agent (spec 021). */
type VerdictStatus = "REJECTED" | "AWAITING_APPROVAL";

function isVerdict(
  slot: DossierSlot,
): slot is DossierSlot & { status: VerdictStatus } {
  return slot.status === "REJECTED" || slot.status === "AWAITING_APPROVAL";
}

const VERDICT_PILL: Record<VerdictStatus, { label: string; tone: StatusTone }> =
  {
    REJECTED: { label: "Needs replacement", tone: "danger" },
    AWAITING_APPROVAL: { label: "Waiting for your approval", tone: "warning" },
  };

/** What happened to a discarded document, in the agent's words (spec 017). */
function discardLabel(doc: Document): string {
  if (doc.discard_reason === "REPLACED") return "Replaced";
  if (doc.discarded_by_role === "client" || doc.discarded_by_role === "link")
    return "Discarded by client";
  return "Discarded by agent";
}

export function ClientDetail() {
  const { clientId = "" } = useParams<{ clientId: string }>();
  const [toast, setToast] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  // Local mirror of the documents query so approve/reject reflect instantly.
  const [docs, setDocs] = useState<Document[]>([]);
  const [busyDoc, setBusyDoc] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const clientQuery = useQuery<ClientDetailRow>({
    queryKey: ["client", clientId],
    queryFn: () => clientService.get(api, clientId),
    retry: false,
  });

  const documentsQuery = useQuery<Document[]>({
    queryKey: ["client-documents", clientId],
    // Spec 017: discarded / replaced documents are hidden by default; the agent
    // asks for them, as history.
    queryFn: () =>
      documentService.list(api, {
        client_id: clientId,
        limit: 200,
        include_discarded: true,
      }),
    enabled: clientQuery.isSuccess,
  });

  const dossiersQuery = useQuery<DossierSummary[]>({
    queryKey: ["client-dossiers", clientId],
    queryFn: () => dossierService.list(api, { client_id: clientId, limit: 50 }),
    enabled: clientQuery.isSuccess,
  });

  // Spec 021 — the checklist item's verdict lives on the dossier slot, not on
  // the document: an AI rejection leaves the document PROCESSED/CLASSIFIED and
  // writes only the slot's status and reason. The staff dossier route already
  // returns both, scoped exactly like the dossier list above, so read it there
  // rather than adding them to DocumentOut, which clients and signed-link
  // holders also receive.
  const dossierList = dossiersQuery.data ?? [];
  const detailQueries = useQueries({
    queries: dossierList.map((d) => ({
      queryKey: ["dossier-detail", d.id],
      queryFn: () => dossierService.get(api, d.id),
      retry: false,
    })),
  });
  // A failed detail read means "no verdicts known" for that application —
  // never an error page; the documents still list.
  const slotsByDossier = new Map<string, readonly DossierSlot[]>();
  detailQueries.forEach((q, i) => {
    const d = dossierList[i];
    const detail = q.data as Dossier | undefined;
    if (d && detail) slotsByDossier.set(d.id, detail.slots);
  });

  /** Items in this application that this document fills and that need the
   *  agent: rejected, or waiting for approval. */
  function verdictsFor(
    dossierId: string | null | undefined,
    documentId: string,
  ): Array<DossierSlot & { status: VerdictStatus }> {
    if (!dossierId) return [];
    return (slotsByDossier.get(dossierId) ?? [])
      .filter((sl) => sl.document_id === documentId)
      .filter(isVerdict);
  }

  /** Classification badge OR a slot verdict. Unfiled documents have no
   *  application, so for them this is the classification rule alone. */
  function needsReview(doc: Document): boolean {
    return (
      reviewBadge(doc.classification_status) !== null ||
      verdictsFor(doc.dossier_id, doc.id).length > 0
    );
  }

  useEffect(() => {
    if (documentsQuery.data) setDocs(documentsQuery.data);
  }, [documentsQuery.data]);

  async function view(documentId: string) {
    setOpeningId(documentId);
    try {
      await openDocument(api, documentId);
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Couldn't open that file.");
    } finally {
      setOpeningId(null);
    }
  }

  function patchDoc(id: string, status: ClassificationStatus) {
    setDocs((ds) =>
      ds.map((d) =>
        d.id === id ? { ...d, classification_status: status } : d,
      ),
    );
  }

  /** Accept the document into the dossier (confirm the current/declared type). */
  async function approve(doc: Document) {
    setBusyDoc(doc.id);
    try {
      await documentService.classify(api, doc.id, {
        action: "confirm_declared",
      });
      patchDoc(doc.id, "CLASSIFIED");
      setToast(`Approved ${doc.original_filename ?? "document"}.`);
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Couldn't approve.");
    } finally {
      setBusyDoc(null);
    }
  }

  /**
   * Reject the document — bounces its dossier slot back to the client for a
   * re-upload. Needs the slot linkage document-svc returns alongside the row.
   */
  async function reject(doc: Document) {
    const slot = doc;
    if (!slot.dossier_id || !slot.template_item_id) {
      setToast("This document isn't linked to a dossier slot to reject.");
      return;
    }
    setBusyDoc(doc.id);
    try {
      await dossierService.decideSlot(
        api,
        slot.dossier_id,
        slot.template_item_id,
        {
          decision: "reject",
          reason: "Rejected on review — re-upload requested.",
          // Spec 019: reject THIS document, not a replacement it never saw.
          document_id: doc.id,
        },
      );
      // The verdict is the slot's: reload it rather than inventing a document
      // status no server writes (REJECTED_BY_HUMAN had no writer).
      await queryClient.invalidateQueries({
        queryKey: ["dossier-detail", slot.dossier_id],
      });
      void documentsQuery.refetch();
      setToast(
        `Rejected ${doc.original_filename ?? "document"} — re-upload requested.`,
      );
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Couldn't reject.");
      // 409: the client replaced it meanwhile — reload what is there now.
      if (err instanceof ApiClientError && err.status === 409)
        void documentsQuery.refetch();
    } finally {
      setBusyDoc(null);
    }
  }

  /**
   * "Uploaded by mistake", on the client's behalf (spec 017). The document
   * stops counting and leaves the client's view; every item it filled asks for
   * the document again. Nothing is deleted — it moves to history below. The
   * server refuses a required item of a finished application; its message is
   * shown as is.
   */
  async function discard(doc: Document) {
    const name = doc.original_filename ?? "this document";
    if (
      !window.confirm(
        `Mark "${name}" as uploaded by mistake? It stops counting toward the client's application, and they'll be asked for the right document.`,
      )
    )
      return;
    setBusyDoc(doc.id);
    try {
      await documentService.discard(api, doc.id);
      await documentsQuery.refetch();
      setToast(`${name} marked as uploaded by mistake.`);
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Couldn't remove it.");
    } finally {
      setBusyDoc(null);
    }
  }

  if (clientQuery.isLoading) {
    return <p style={{ padding: 24 }}>Loading…</p>;
  }
  if (clientQuery.isError || !clientQuery.data) {
    // A client outside the caller's scope 404s exactly like a missing one —
    // deliberate on the server, so don't invent a distinction here.
    return (
      <div style={{ padding: 24 }}>
        <p>We couldn't find that client, or they're not in your scope.</p>
        <Link to="/clients">← Back to clients</Link>
      </div>
    );
  }

  const client = clientQuery.data;
  const address = formatAddress(client);
  // History (spec 017) is listed on its own, never as a live document.
  const discarded = docs.filter((d) => d.status === "DISCARDED");
  // Flagged docs float to the top so the review reason is the first thing seen.
  const documents = docs
    .filter((d) => d.status !== "DISCARDED")
    .sort((a, b) => (needsReview(b) ? 1 : 0) - (needsReview(a) ? 1 : 0));
  const dossiers = dossierList;
  // A bulk-inbox drop has no slot until somebody files it, so it belongs to no
  // application and would vanish if the only list were the grouped one.
  const unfiled = documents.filter((d) => !d.dossier_id);
  // The Unfiled header counts UNFILED documents only. It used to count every
  // live document, so a flagged one inside an application was counted twice
  // on the page (spec 021, audit N5).
  const flaggedCount = unfiled.filter(needsReview).length;
  const coClientName = [client.co_client_first_name, client.co_client_last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  /** One document row. Rendered under its application, and in the unfiled
   *  list for a bulk-inbox drop nobody has filed yet. Declared inside the
   *  component because every action it offers — view, approve, reject —
   *  closes over this screen's own mutation state. */
  function DocumentRow({ doc }: { doc: Document }) {
    const verdicts = verdictsFor(doc.dossier_id, doc.id);
    return (
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
          fontSize: 13.5,
          paddingBottom: 8,
          borderBottom: "1px solid var(--rp-color-border)",
        }}
      >
        <span>{doc.original_filename ?? doc.id.slice(0, 8)}</span>
        {doc.doc_type && (
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            {doc.doc_type}
          </span>
        )}
        <StatusPill tone={doc.status === "PROCESSED" ? "success" : "info"}>
          {doc.status}
        </StatusPill>
        {(() => {
          const badge = reviewBadge(doc.classification_status);
          return badge ? (
            <StatusPill tone={badge.tone}>{badge.label}</StatusPill>
          ) : null;
        })()}
        {verdicts.map((v) => (
          <StatusPill key={v.id} tone={VERDICT_PILL[v.status].tone}>
            {verdicts.length > 1 ? `${v.display_name}: ` : ""}
            {VERDICT_PILL[v.status].label}
          </StatusPill>
        ))}
        {/* The decision itself happens on Document review (FR-005): this page
            says what is wrong and where to act, it does not add a second
            approve/replace path. */}
        {verdicts.length > 0 && (
          <Link to="/document-review" style={{ fontSize: 13 }}>
            Open in Document review
          </Link>
        )}
        <span
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          {(doc.classification_status === "DECLARED_MISMATCH" ||
            doc.classification_status === "CLASSIFIED_PENDING_REVIEW") && (
            <>
              <Button
                size="sm"
                variant="primary"
                disabled={busyDoc === doc.id}
                title="Accept this document into the dossier"
                onClick={() => void approve(doc)}
              >
                {busyDoc === doc.id ? "Working…" : "Approve"}
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={busyDoc === doc.id}
                title="Reject and ask the client to re-upload"
                onClick={() => void reject(doc)}
              >
                Re-upload
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="ghost"
            disabled={busyDoc === doc.id}
            title="The client uploaded the wrong file — stop counting it"
            onClick={() => void discard(doc)}
          >
            Mark as mistake…
          </Button>
          <Button
            size="sm"
            variant="secondary"
            // `s3_uploaded_at` is NULL until the browser's PUT actually
            // lands, so the row can exist with no object behind it —
            // the server 409s, so don't offer the button at all.
            disabled={!doc.s3_uploaded_at || openingId === doc.id}
            title={
              doc.s3_uploaded_at
                ? "Open the file"
                : "This upload never finished"
            }
            onClick={() => void view(doc.id)}
          >
            {openingId === doc.id ? "Opening…" : "View"}
          </Button>
        </span>
        {/* The reason, when there is one — the V074 "waiting for approval"
            path writes none, and saying "null" would be worse than nothing. */}
        {verdicts
          .filter((v) => v.rejection_reason)
          .map((v) => (
            <div
              key={`${v.id}-reason`}
              role="note"
              style={{
                flexBasis: "100%",
                color: "var(--rp-color-text-muted)",
                fontSize: 13,
                overflowWrap: "anywhere",
              }}
            >
              {verdicts.length > 1 ? `${v.display_name}: ` : ""}
              {v.rejection_reason}
            </div>
          ))}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Link to="/clients" style={{ fontSize: 13 }}>
          ← Clients
        </Link>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 6,
          }}
        >
          <h1 style={{ margin: 0, fontSize: 22 }}>{fullName(client)}</h1>
          <ContactButtons email={client.email} phone={client.phone_e164} />
        </div>
      </div>

      <Card padding="lg">
        <h2 style={{ margin: "0 0 12px", fontSize: 16 }}>Contact</h2>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <Field label="Email" value={client.email} />
          <Field label="Phone" value={formatPhone(client.phone_e164 ?? "")} />
          {/* Nothing ever ASKS the client for their address — it is read off
              their driver's licence, and it is what the representation
              agreement prints. Say where it came from rather than presenting
              an OCR result as confirmed fact. */}
          <Field
            label="Address"
            value={address}
            note="From their driver's licence"
          />
          <Field label="Marital status" value={client.marital_status} />
          <Field label="Employment" value={client.employment_status} />
          <Field label="Client since" value={client.created_at.slice(0, 10)} />
        </div>
      </Card>

      {coClientName && (
        <Card padding="lg">
          <h2 style={{ margin: "0 0 4px", fontSize: 16 }}>Co-client</h2>
          <p
            style={{
              margin: "0 0 12px",
              color: "var(--rp-color-text-muted)",
              fontSize: 13.5,
            }}
          >
            Signs the representation agreement as a second remote recipient — a
            missing email here means their signature can never be collected.
          </p>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <Field label="Name" value={coClientName} />
            <Field label="Relationship" value={client.co_client_relationship} />
            <Field label="Email" value={client.co_client_email} />
            <Field
              label="Phone"
              value={formatPhone(client.co_client_phone_e164 ?? "")}
            />
          </div>
        </Card>
      )}

      {/* Applications, each holding the documents uploaded into it.
          Two flat lists side by side left the agent matching filenames against
          applications by eye — and a client with two listings has two sets of
          identically-named documents. Grouped and collapsible, so one
          application can be read without scrolling past the others. */}
      <Card padding="lg">
        <h2 style={{ margin: "0 0 12px", fontSize: 16 }}>
          Applications ({dossiers.length})
        </h2>
        {dossiers.length === 0 ? (
          <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13.5 }}>
            No applications yet.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {dossiers.map((d, i) => {
              const own = documents.filter((doc) => doc.dossier_id === d.id);
              const needing = own.filter(needsReview).length;
              return (
                <details
                  key={d.id}
                  className="rp-appgroup"
                  // The first application open, the rest closed: an agent opening
                  // a client is nearly always looking at the newest thing, and
                  // one open group shows the shape without hiding everything.
                  open={i === 0}
                >
                  <summary className="rp-appgroup__summary">
                    <span className="rp-appgroup__title">
                      {d.template_key ?? d.id.slice(0, 8)}
                    </span>
                    <StatusPill tone="info">{d.status}</StatusPill>
                    {needing > 0 && (
                      <StatusPill tone="warning">
                        {needing} need{needing === 1 ? "s" : ""} review
                      </StatusPill>
                    )}
                    <span className="rp-appgroup__count">
                      {own.length} document{own.length === 1 ? "" : "s"}
                    </span>
                    <Link
                      to={`/dossiers/active`}
                      style={{ fontSize: 13 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Open in Active
                    </Link>
                  </summary>
                  {own.length === 0 ? (
                    <p
                      style={{
                        color: "var(--rp-color-text-muted)",
                        fontSize: 13.5,
                        margin: "4px 0 8px",
                      }}
                    >
                      Nothing uploaded into this application yet.
                    </p>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {own.map((doc) => (
                        <DocumentRow key={doc.id} doc={doc} />
                      ))}
                    </div>
                  )}
                </details>
              );
            })}
          </div>
        )}
      </Card>

      <Card padding="lg">
        <h2
          style={{
            margin: "0 0 12px",
            fontSize: 16,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          Unfiled documents ({unfiled.length})
          {flaggedCount > 0 && (
            <StatusPill tone="warning">
              {flaggedCount} need{flaggedCount === 1 ? "s" : ""} review
            </StatusPill>
          )}
        </h2>
        {documentsQuery.isLoading ? (
          <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13.5 }}>
            Loading…
          </p>
        ) : unfiled.length === 0 ? (
          <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13.5 }}>
            Everything they've uploaded is filed against an application above.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {unfiled.map((doc) => (
              <DocumentRow key={doc.id} doc={doc} />
            ))}
          </div>
        )}
      </Card>

      {discarded.length > 0 && (
        <Card padding="lg">
          <h2 style={{ margin: "0 0 4px", fontSize: 16 }}>
            Discarded and replaced ({discarded.length})
          </h2>
          <p
            style={{
              margin: "0 0 12px",
              color: "var(--rp-color-text-muted)",
              fontSize: 13.5,
            }}
          >
            These no longer count toward any application and the client no
            longer sees them. Kept for your records.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {discarded.map((doc) => (
              <div
                key={doc.id}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  flexWrap: "wrap",
                  fontSize: 13.5,
                  paddingBottom: 8,
                  borderBottom: "1px solid var(--rp-color-border)",
                }}
              >
                <span>{doc.original_filename ?? doc.id.slice(0, 8)}</span>
                <StatusPill tone="neutral">{discardLabel(doc)}</StatusPill>
                {doc.discarded_at && (
                  <span style={{ color: "var(--rp-color-text-muted)" }}>
                    {new Date(doc.discarded_at).toLocaleString()}
                  </span>
                )}
                <span style={{ marginLeft: "auto" }}>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!doc.s3_uploaded_at || openingId === doc.id}
                    onClick={() => void view(doc.id)}
                  >
                    {openingId === doc.id ? "Opening…" : "View"}
                  </Button>
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
